import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Stack, FormControlLabel, RadioGroup, Radio, Box, useTheme, Tooltip } from '@mui/material'
import { z } from 'zod'

import { GenericDialogLayout } from '../GenericDialogLayout'
import { FormSelectField, AppForm } from '../FormCore'
import { MainButton } from '../MainButton'
import { CancelButton } from '../CancelButton'
import BasicDatePicker from '../DateTimePicker'
import { CommonPageLayout } from '../common/CommonPageLayout'
import { ShowDialogProps } from 'src/show-dialog'
import { useZodForm } from 'src/hooks/zod-form'
import { useGenerateReport, useGetReportTemplatesByFormSchemaVersion } from 'src/api/report-template'
import { downloadFileLocal } from 'src/utils/downloadFile'
import { GenerateReportDateStatus } from 'src/types/report-designer'
import { CreateReportTemplateDialog } from './CreateReportTemplateDialog'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { toast } from 'react-toastify'
import { ReportTemplateStatus } from 'src/enums'
import { getThemeColor } from 'src/utils'
import { MUIThemeColors } from 'src/mui-theme'
import { WarningAmberOutlined } from '@mui/icons-material'

interface IGenerateReportModalProps extends ShowDialogProps {
  customerId?: number
  reportTemplateId?: number
  openedFromTemplateRow?: boolean
  setGeneratingTemplateId?: (id: number | null) => void
  selectedTemplate?: ITemplateOption
  formSchemaVersionId?: number
  formSchemaId: number
  isFormSchemaActive?: boolean
}

export interface ITemplateOption {
  id: number
  name: string
  status?: ReportTemplateStatus
}

const GenerateReportModal: React.FC<IGenerateReportModalProps> = ({
  pop,
  customerId,
  openedFromTemplateRow = false,
  setGeneratingTemplateId,
  selectedTemplate,
  formSchemaVersionId,
  formSchemaId,
  isFormSchemaActive,
}) => {
  const { t: translate } = useTranslation(['dynamic-report', 'common', 'common-api-message'])

  const generateReportSchema = z
    .object({
      template: z
        .object({
          id: z.number(),
          name: z.string(),
        })
        .nullable()
        .refine(val => val !== null, { message: 'Template is required' }),
      dateRangeType: z.number(),
      fromDate: z.date().nullable().optional(),
      toDate: z.date().nullable().optional(),
    })
    .superRefine(({ fromDate, toDate }, ctx) => {
      if (fromDate && toDate && toDate < fromDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: translate('to-date-must-be-after-from-date'),
          path: ['toDate'],
        })
      }
    })

  const { mutateAsync: generateReport, isLoading } = useGenerateReport(customerId as EntityId)
  const { data: templateOptions, isLoading: isTemplatesLoading } = useGetReportTemplatesByFormSchemaVersion(
    customerId as EntityId,
    (formSchemaVersionId as EntityId) ?? 0,
    formSchemaId as EntityId,
  )
  const { showDialog } = useDialogCtx()
  const theme = useTheme()

  const form = useZodForm(generateReportSchema, {
    defaultValues: {
      template: selectedTemplate,
      dateRangeType: GenerateReportDateStatus.all,
      fromDate: null,
      toDate: null,
    },
  })

  const { watch, setValue } = form
  const dateRangeType = watch('dateRangeType')
  const fromDate = watch('fromDate')
  const toDate = watch('toDate')

  const handleGenerateTemplate = formData => {
    setGeneratingTemplateId && setGeneratingTemplateId(formData.template?.id)
    generateReport(
      {
        templateId: formData.template?.id,
        formSchemaId: formSchemaId as EntityId,
        userTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        ...formData,
      },
      {
        onSuccess: res => {
          downloadFileLocal(res, `${formData.template?.name}-${new Date().toISOString().split('T')[0]}`)
          setGeneratingTemplateId && setGeneratingTemplateId(null)
          toast.success(translate('report-generated-successfully', { ns: 'dynamic-report' }))
          pop()
        },
        onError: () => {
          setGeneratingTemplateId && setGeneratingTemplateId(null)
          toast.error(translate('error-generating-report', { ns: 'dynamic-report' }))
        },
      },
    )
  }

  const handleCreateTemplate = useCallback(async () => {
    await showDialog(
      CreateReportTemplateDialog,
      {
        componentProps: {
          customerId: customerId as EntityId,
          formSchemaId: formSchemaId ?? undefined,
        },
        opts: {
          maxWidth: 'sm',
          fullWidth: true,
          PaperProps: {
            sx: {
              maxHeight: '90vh',
            },
          },
        },
      },
      undefined,
      true,
    )
  }, [customerId, showDialog, formSchemaId])

  const handleDateRangeSelection = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value === GenerateReportDateStatus.all.toString()) {
      setValue('fromDate', null)
      setValue('toDate', null)
    }
    setValue('dateRangeType', Number(e.target.value))
  }

  // Template selection section - only visible if not opened from templateRow
  const templateSelectionContent = !openedFromTemplateRow && (
    <CommonPageLayout
      titleSection={{
        subtitle: translate('select-existing-report-template', { ns: 'dynamic-report' }),
        subtitleFontSize: '14px',
      }}
      topAction={
        isFormSchemaActive
          ? [
              {
                buttonText: translate('new-template', { ns: 'dynamic-report' }),
                variant: 'outlined',
                onClick: handleCreateTemplate,
                key: 'new-template-btn',
                sx: {
                  border: 'none',
                  '&:hover': { border: 'none', backgroundColor: 'transparent' },
                },
              },
            ]
          : []
      }
      childrenProps={{ sx: { gap: 2 } }}
      titleContainerProps={{ sx: { pb: 1, pt: 0 } }}
    >
      <FormSelectField<ITemplateOption>
        name="template"
        label={translate('select-template', { ns: 'dynamic-report' })}
        data={templateOptions ?? []}
        getOptionLabel={option => (typeof option === 'string' ? option : option.name)}
        isOptionEqualToValue={(option, value) => {
          if (!value) return false
          return option.id === value.id
        }}
        loading={isTemplatesLoading}
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            <Box display="flex" alignItems="center" gap={1}>
              <span>{option.name}</span>
              {option.status === ReportTemplateStatus.Warning && (
                <Tooltip title={translate('version-upgrade-warning-intro', { ns: 'dynamic-report' }) as string}>
                  <WarningAmberOutlined
                    sx={{ color: getThemeColor(theme, MUIThemeColors.warningLight), fontSize: '16px' }}
                  />
                </Tooltip>
              )}
            </Box>
          </li>
        )}
      />
    </CommonPageLayout>
  )

  const dateRangeContent = (
    <CommonPageLayout
      titleSection={{
        subtitle: translate('select-date-range', { ns: 'dynamic-report' }),
        subtitleFontSize: '14px',
      }}
      childrenProps={{ sx: { gap: 2 } }}
      titleContainerProps={{ sx: { pb: 1, pt: 0 } }}
    >
      <RadioGroup value={dateRangeType} onChange={e => handleDateRangeSelection(e)} row sx={{ gap: 2 }}>
        <FormControlLabel
          value={GenerateReportDateStatus.all}
          control={<Radio />}
          label={translate('include-all-reports', { ns: 'dynamic-report' }) as string}
          key={'all'}
        />
        <FormControlLabel
          value={GenerateReportDateStatus.custom}
          control={<Radio />}
          label={translate('select-custom-date-range', { ns: 'dynamic-report' }) as string}
          key={'custom'}
        />
      </RadioGroup>

      {dateRangeType === GenerateReportDateStatus.custom && (
        <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
          <Box sx={{ flex: 1 }}>
            <BasicDatePicker
              name="fromDate"
              label={translate('from-date')}
              allowNullable
              onChange={date => {
                setValue('fromDate', date)
                if (date && toDate && date > toDate) {
                  setValue('toDate', null)
                }
              }}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <BasicDatePicker name="toDate" label={translate('to-date')} allowNullable minDate={fromDate ?? undefined} />
          </Box>
        </Stack>
      )}
    </CommonPageLayout>
  )

  return (
    <AppForm form={form} onSubmit={handleGenerateTemplate}>
      <GenericDialogLayout
        title={translate('generate-report', { ns: 'dynamic-report' })}
        pop={pop}
        actions={[
          <CancelButton onClick={pop} key={'cancel'} />,
          <MainButton onClick={form.handleSubmit(handleGenerateTemplate)} key={'generate'} loading={isLoading}>
            {translate('generate', { ns: 'dynamic-report' })}
          </MainButton>,
        ]}
      >
        <Stack spacing={3} sx={{ width: '100%', paddingLeft: 1 }}>
          {templateSelectionContent}
          {dateRangeContent}
        </Stack>
      </GenericDialogLayout>
    </AppForm>
  )
}

export default GenerateReportModal
